<template>
  <div class="source-content themes">
    <div class="p-[0.625rem]">
      <search-filter
        :value="search"
        :options="sortedSocialDisplayTags"
        :selected="selectedTags"
        data-test="producer-assets-displayElements-searchFilter"
        @update:value="changeSocialDisplaySearch"
        @update:selected="onUpdateSelectedTags"
      />
    </div>

    <!-- No themes after searching -->
    <div v-if="!isLoading && !total" class="py-8 text-center">
      <h3 class="mb-4 text-[14px] font-semibold">
        No themes found
      </h3>

      <button
        class="tb-btn tb-btn-contained medium-em px-3"
        data-entity="producer-assets-resetThemeSearch"
        data-test="producer-assets-displayElements-resetSearch"
        @click="clearTags"
      >
        Reset Search
      </button>
    </div>

    <div
      ref="sourceList"
      class="source-list"
      data-test="producer-assets-displayElements-list"
    >
      <draggable
        :list="themes"
        :clone="clone"
        :sort="false"
        animation="200"
        :group="{ name: 'timeline', pull: 'clone', put: false }"
        revert-on-spill="true"
        @start="startDrag"
        @end="$store.dispatch('clearDragType')"
      >
        <div
          v-for="theme in themes"
          class="source-item theme"
          :data-template-id="theme._id"
          data-entity="producer-assets-theme"
          :data-test="`producer-assets-element:${theme._id}`"
          :key="theme._id"
        >
          <div class="tb-checkbox">
            <input
              type="checkbox"
              :id="theme._id"
              :value="theme"
              v-model="selected"
              data-entity="producer-assets-toggleThemeSelection"
              :data-test="`producer-assets-element-element:${theme._id}`"
            >

            <label :for="theme._id" />
          </div>

          <div class="source-item-wrapper">
            <img class="preview mr-2" :src="theme.preview_url" :data-test="`producer-assets-element-element-preview:${theme._id}`" />

            <div class="source-item-info-wrapper">
              <div class="source-title title-text" :data-test="`producer-assets-element-title:${theme._id}`">
                {{ theme.title }}
              </div>

              <div
                :class="{
                  'source-item-tags': true,
                  'mt-1': theme.tags?.length,
                }"
                :data-test="`producer-assets-element-tags:${theme._id}`"
              >
                <span
                  v-for="tag in theme.tags"
                  :key="tag"
                  class="tag"
                  :data-test="`producer-assets-element-tags:${theme._id}_${tag}`"
                >
                  {{ tag }}
                </span>

                <tag-overflow-popover
                  v-if="theme.tags?.length"
                  :tags="theme.tags"
                  :data-test="`producer-assets-element-tags:${theme._id}_overflow`"
                />
              </div>
            </div>

            <div class="source-description description-text capitalize" :data-test="`producer-assets-element-layout:${theme._id}`">
              {{ theme.layout }}
            </div>

            <div class="source-actions ml-1">
              <button
                class="tb-btn tb-btn-contained sm medium-em add-to-playlist"
                title="Add to playlist"
                data-entity="producer-assets-addThemeToPlaylist"
                :data-test="`producer-assets-element-addToPlaylist:${theme._id}`"
                @click="addToPlaylist(theme, 'Theme')"
              >
                <span class="fas fa-plus" />
              </button>
            </div>
          </div>
        </div>
      </draggable>

      <div ref="loadTrigger" class="load-trigger flex min-h-16 w-full items-center justify-center">
        <div v-if="isLoading" class="loading loading--inline m-0" data-test="producer-assets-displayElements-loading">
          <i class="fas fa-spinner fa-spin"></i>
        </div>
      </div>
    </div>

    <div
      v-if="selected.length"
      class="producer-assets-selected flex items-center justify-end p-4"
      data-test="producer-assets-selected"
    >
      <p class="producer-assets-selected__count mb-0 mr-4 mt-0 text-primary" data-test="producer-assets-selected-count">{{ selected.length }} selected</p>

      <button
        class="tb-btn tb-btn-contained medium-em ml-4"
        :class="{'disabled': !selected.length }"
        :disabled="!selected.length"
        data-entity="producer-assets-addSelectedPanelsToPlaylist"
        data-test="producer-assets-selected-addToPlaylist"
        @click="addSelectedToPlaylist('Panels')"
      >
        Add to playlist
      </button>
    </div>
  </div>
</template>

<script>
import * as uuid from 'uuid';
import { mapState, mapActions } from 'vuex';
import { pick } from 'lodash-es';

import SearchFilter from '@/js/shared/SearchFilter.vue';
import TagOverflowPopover from '@/js/shared/TagOverflowPopover.vue';
import tabMixins from '../../mixins/tab-mixins';

import toast from '../../components/toast/toast';

export default {
  mixins: [tabMixins],

  components: {
    SearchFilter,
    TagOverflowPopover,
  },
  data() {
    return {
      selected: [],
    };
  },

  created() {
    this.getSocialDisplayTags();
    this.$store.dispatch('getThemes').catch((err) => {
      console.error('Failed to load themes:', err);

      toast.makeToast({
        heading: 'Error',
        icon: 'error',
        loaderBg: 'red',
        text: 'Failed to load themes.',
      });
    });
  },

  mounted() {
    if (this.$refs.sourceList) {
      this.intersectionObserver = new IntersectionObserver(this.loadMore, {
        root: this.$refs.sourceList,
      });

      this.intersectionObserver.observe(this.$refs.loadTrigger);
    }
  },

  watch: {
    themes() {
      if (!this.$refs.sourceList || !this.themes.length) {
        return;
      }

      if (this.$refs.sourceList.scrollHeight <= this.$refs.sourceList.offsetHeight) {
        // List isn't scrollable, so try to load more right away
        // Otherwise the intersection observer will never trigger it's callback
        this.loadMore();
      }
    },
  },

  methods: {
    ...mapActions([
      'getSocialDisplayTags',
      'changeSocialDisplaySearch',
      'changeSocialDisplayTags',
      'resetSocialDisplaySearch',
    ]),

    onUpdateSelectedTags(tags) {
      this.changeSocialDisplayTags(tags);
    },

    displayTags(tags = []) {
      return tags.slice(0, 2);
    },

    tagOverflowCount(tags = []) {
      return Math.max((tags?.length || 0) - 2, 0);
    },

    clearTags() {
      this.resetSocialDisplaySearch();
    },

    /**
    * Attempt to load more themes
    */
    loadMore() {
      if (!this.isLoading && this.$store.getters.hasMoreThemes) {
        this.$store.dispatch('incrementThemesPage');
      }
    },

    startDrag(e) {
      const { item } = e;
      const { templateId } = item.dataset;

      const grabGroup = !!this.selected.find((t) => t._id === parseInt(templateId, 10));
      this.$store.dispatch('setDragType', {
        type: 'theme',
        group: grabGroup ? this.selected.map(this.clone) : null,
      });
    },

    clone(original) {
      const clone = pick(original, ['_id', 'title', 'layout']);
      clone.posts = [];
      clone.fields = {};
      clone.timeline_id = uuid.v1();
      clone.displayTime = 30000;
      clone.transition = 'Cut'; // legacy but keep for backwards compatibility during testing
      clone.queuedPost = null;
      return clone;
    },

    reloadThemes() {
      this.$store.dispatch('resetThemes');
      this.$store.dispatch('getThemes');
    },
  },

  computed: {
    ...mapState({
      search: (state) => state.themes.search,
      selectedTags: (state) => state.themes.tags,
      isLoading: (state) => state.themes.isLoading,
      total: (state) => state.themes.total,
      themes: (state) => state.themes.themes,
      sortedSocialDisplayTags: (state) => state.themes.socialDisplayTags,
    }),
  },
};
</script>
